import { FeatureFlag } from '@ps/web-core-enums';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';
import type { Row, TableCellProps, TableRowProps } from 'react-table';

import useFeature from '@swf/core/hooks/useFeature';
import { useHGGameUrl, useHomeGamesWidget, useOpenGame } from '@swf/poker';

import type { HGPokerGameTile } from '../../../types/common';
import { HGGameType } from '../../../types/common';

import styles from './GameListItem.scss';

export interface Props {
  row: Row<HGPokerGameTile>;
  rowProps: TableRowProps;
}

const GameListItem: React.FC<Props> = ({ row, rowProps }) => {
  const { type, server, serverObj, supported, link, id, clubId, hasVideoSupport } = row.original;
  const tableUrl = useHGGameUrl({
    type,
    serverAddress: server,
    serverObjectName: serverObj,
    hasVideoSupport
  });
  const unsupported =
    useFeature(FeatureFlag.PKRHomeGamesGameTilesUnsupported) ||
    (typeof supported === 'boolean' && !supported);
  const { widgetSelect } = useHomeGamesWidget();
  const handleOnGameClicked = useOpenGame({ tableUrl, unsupported, id, link, clubId });

  const isCashGamesActive = useSelector(widgetSelect.isCashGameActive);
  const isTournsActive = useSelector(widgetSelect.isTournsActive);

  const isCashGame = type === HGGameType.CashGame;
  const isTournament = type === HGGameType.Tournament;

  return (
    <tr
      {...rowProps}
      data-testid={`game-list-item-${id}`}
      onClick={handleOnGameClicked}
      className={classNames(styles.row, {
        [styles.unsupported]:
          unsupported || (isCashGame && !isCashGamesActive) || (isTournament && !isTournsActive)
      })}
    >
      {row.cells.map((cell) => {
        const cellProps: TableCellProps = cell.getCellProps();
        return (
          <td {...cellProps} key={cellProps.key}>
            {cell.render('Cell')}
          </td>
        );
      })}
    </tr>
  );
};

export default GameListItem;
